import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  assignWith,
  cloneDeep,
  forEach,
  fromPairs,
  isString,
  map,
  pick,
  uniqWith,
} from 'lodash';
import classnames from 'classnames';

import { titleCase } from 'js/utils/string';
import { timeAgo } from 'js/utils/time-ago';

import { Card, CardHeader, CardBody, CardTitle, Button } from 'reactstrap';
import { Link } from 'react-router-dom';
import { PanelHeader, Icon } from 'js/components';
import { TriStateSwitch } from 'js/components/Forms/TriStateSwitch';
import AsyncButton from 'js/components/Forms/AsyncButton';
import { InlineAlert } from 'js/components/InlineAlert/InlineAlert';
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import {
  SELECTION,
  countIncluded,
  questionId,
  questionSetIds,
  selectQuestionSets,
  selectionState,
  toggleGroup,
} from 'js/utils/question-selection';
import ConfigureQuestionSet from './ConfigureQuestionSet';
import ConfigurationOverview from './ConfigurationOverview';

const generateCategoryReference = (template) => {
  const baseCategoryReference = cloneDeep(template.config?.categories ?? {});

  const extractedCategoryReference = fromPairs(
    uniqWith(
      template.assessment.question_sets,
      (a, b) => a.category === b.category
    ).map((qs) => {
      const uncategorized = !isString(qs.category);

      if (uncategorized) {
        return ['uncategorized', { display_name: 'Uncategorized' }];
      }

      return [qs.category, { display_name: titleCase(qs.category) }];
    })
  );

  let paletteIndex = 0;

  // This mess preserves the palette based on the ordering from the categories,
  // but only shows the categories that are actually used in the template
  const fullCategoryReference = assignWith(
    baseCategoryReference,
    extractedCategoryReference,
    (obj, src) => {
      return obj === undefined ? src : obj;
    }
  );

  forEach(fullCategoryReference, (val) => {
    val.paletteClass = `palette-cat-${++paletteIndex}`;
  });

  const categoryReference = pick(
    fullCategoryReference,
    Object.keys(extractedCategoryReference)
  );

  return categoryReference;
};

const categoryQuestionIds = (questionSets) => {
  const ids = {};

  questionSets.forEach((qs, setIndex) => {
    const category = qs.category || 'uncategorized';

    ids[category] = [...(ids[category] ?? []), ...questionSetIds(qs, setIndex)];
  });

  return ids;
};

function baseGenerateAssessmentForCreate(template, excluded) {
  return {
    ...template.assessment,
    question_sets: selectQuestionSets(
      template.assessment.question_sets,
      excluded
    ),
    config: template.config || null,
    type: template.type,
  };
}

const ConfigureSurveyAssessment = ({
  template,
  team,
  workspaceId,
  creating,
  generateAssessmentForCreate = baseGenerateAssessmentForCreate,
  onCreateAssessment,
  draft,
  onCustomize,
  onResumeDraft,
  onDiscardDraft,
}) => {
  const questionSets = template.assessment.question_sets;

  const categoryReference = useMemo(
    () => generateCategoryReference(template),
    [template]
  );
  const categoryIds = useMemo(
    () => categoryQuestionIds(questionSets),
    [questionSets]
  );

  // A single topic would just repeat the question set switches.
  const showTopics = Object.keys(categoryReference).length > 1;

  const [excluded, setExcluded] = useState(() => new Set());

  const toggleQuestions = (ids) => {
    setExcluded((current) => toggleGroup(ids, current));
  };

  const handleCreateAssessment = (e) => {
    e.preventDefault();

    const assessment = generateAssessmentForCreate(template, excluded);

    onCreateAssessment(assessment);
  };

  const questionCount = countIncluded(questionSets, excluded);

  // The editor starts from what is selected here, and leaves out the rest
  const handleCustomize = () =>
    onCustomize({
      questionSets: selectQuestionSets(questionSets, excluded),
      excludedKeys: new Set(
        questionSets.flatMap((qs, setIndex) =>
          qs.questions
            .filter((_, questionIndex) =>
              excluded.has(questionId(setIndex, questionIndex))
            )
            .map((q) => q.key)
        )
      ),
    });

  return (
    <div id="configure-survey-assessment">
      <PanelHeader size="sm" />
      <div className="content">
        <div className="row">
          <div className="col-9">
            <Card className="configure-survey-assessment-header">
              <CardHeader>
                <label>Configure</label>
                <CardTitle tag="h5">
                  {template.name}
                  <small>
                    for{' '}
                    <Link to={`/w/${workspaceId}/teams/${team.id}/dashboard`}>
                      {team.name}
                    </Link>
                  </small>
                </CardTitle>
              </CardHeader>
              <CardBody>
                {draft && (
                  <InlineAlert
                    icon={faPenToSquare}
                    actions={
                      <>
                        <Button color="link" onClick={onResumeDraft}>
                          Resume
                        </Button>
                        <AsyncButton color="link" onClick={onDiscardDraft}>
                          Discard
                        </AsyncButton>
                      </>
                    }
                  >
                    You started customizing this assessment for {team.name}{' '}
                    {timeAgo(draft.updated_at)}. Pick up where you left off?
                  </InlineAlert>
                )}
                <p>
                  Review the questions and turn off any you don't want to ask
                  this time. You will still have tracking across multiple
                  assessments for a team even if you don't ask the same
                  questions every time.
                </p>

                <label>Question Sets</label>
                <ol className="question-sets">
                  {questionSets.map((qs, setIndex) => (
                    <ConfigureQuestionSet
                      key={`${setIndex}-${qs.key}`}
                      tag="li"
                      questionSet={qs}
                      setIndex={setIndex}
                      excluded={excluded}
                      categoryReference={categoryReference}
                      defaultOpen={questionSets.length === 1}
                      onToggle={toggleQuestions}
                    />
                  ))}
                </ol>
                <div>
                  <form onSubmit={handleCreateAssessment}>
                    <div className="form-actions">
                      <Link
                        disabled={creating}
                        className="btn btn-secondary"
                        to={`/w/${workspaceId}/teams/${team.id}/assessments/new`}
                      >
                        <Icon
                          icon={['fas', 'arrow-left']}
                          className="btn-icon-left"
                        />
                        Back
                      </Link>
                      {onCustomize && (
                        <AsyncButton
                          color="secondary"
                          disabled={creating || questionCount === 0}
                          onClick={handleCustomize}
                        >
                          <Icon
                            icon={['far', 'edit']}
                            className="btn-icon-left"
                          />
                          Customize
                        </AsyncButton>
                      )}
                      <Button
                        type="submit"
                        disabled={creating || questionCount === 0}
                        color="primary"
                      >
                        {creating ? (
                          <Icon
                            icon={['far', 'circle-notch']}
                            className="btn-icon-left"
                            spin
                          />
                        ) : (
                          <Icon
                            icon={['far', 'poll']}
                            className="btn-icon-left"
                          />
                        )}
                        Start
                      </Button>
                    </div>
                  </form>
                </div>
              </CardBody>
            </Card>
          </div>
          <div className="col-3">
            <div className="sticky">
              <ConfigurationOverview questionCount={questionCount} />
              {showTopics && (
                <Card>
                  <CardHeader>
                    <CardTitle tag="h6">Topics</CardTitle>
                  </CardHeader>
                  <CardBody>
                    {map(categoryReference, (value, key) => {
                      const ids = categoryIds[key] ?? [];
                      const state = selectionState(ids, excluded);

                      return (
                        <div
                          key={key}
                          className={classnames({
                            'category-selector': true,
                            muted: state === SELECTION.OFF,
                          })}
                        >
                          <TriStateSwitch
                            id={`category-filter-${key}`}
                            state={state}
                            name={key}
                            onChange={() => toggleQuestions(ids)}
                            label={value.display_name}
                          >
                            <div
                              className={`${value.paletteClass} category-pallete`}
                            />
                          </TriStateSwitch>
                        </div>
                      );
                    })}
                  </CardBody>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ConfigureSurveyAssessment.defaultProps = {
  onCreateAssessment: () => {},
  creating: false,
};

ConfigureSurveyAssessment.propTypes = {
  template: PropTypes.object.isRequired,
  team: PropTypes.object.isRequired,
  workspaceId: PropTypes.number.isRequired,
  onCreateAssessment: PropTypes.func,
  creating: PropTypes.bool,
  draft: PropTypes.object,
  onCustomize: PropTypes.func,
  onResumeDraft: PropTypes.func,
  onDiscardDraft: PropTypes.func,
};

export default ConfigureSurveyAssessment;
